import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useModalContext } from '@/contexts/ModalContext';
import { useAuthCheck, authKeys } from '@/hooks/queries/useAuthQueries';
import { useAuthStore } from '@/stores/authStore';
import { useTwoFactorStore } from '@/stores/twoFactorStore';
import { localStorageUtils } from '@/utils/localStorage';
import { getPoolsUrl } from '@/utils/navigation';
import { sessionStorageUtils } from '@/utils/sessionStorage';

/**
 * AuthNavigator component
 *
 * Single component that handles both:
 * 1. Syncing the React Query auth check result into Zustand (previously AuthRefetcher)
 * 2. Reacting to auth state changes and handling redirects
 *
 * By doing both in the same render cycle we eliminate the timing gap
 * where the query resolved but the store hadn't been updated yet.
 */
export function AuthNavigator() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const queryClient = useQueryClient();
  const hasCheckedInitialLoad = useRef(false);

  const { user, isAuthenticated, setAuthState, clearUser } = useAuthStore();
  const { data: authData, isLoading: authCheckLoading, status: authCheckStatus, error: authError } = useAuthCheck();
  const { openLoginModal, closePoolModal, closeSignupModal, closeUserVerificationModal } = useModalContext();

  const hasSession = localStorageUtils.isSessionAuthenticated();

  // Wait for auth check to complete if there's an active session.
  // If no session flag, we can proceed immediately (no auth check needed).
  const isAuthCheckPending = hasSession && (authCheckLoading || authCheckStatus === 'pending');

  // ── Sync: query result → Zustand store ────────────────────────────
  // Keeps the Zustand auth state in sync with the React Query /api/me result.
  // On success: update user + isAuthenticated in the store.
  // On failure or unauthenticated response: clear session flag, store, and query cache.
  // 401 errors are handled automatically by apiClient's token refresh middleware;
  // if we still get an error here, it means refresh failed or it's a non-401 error.
  useEffect(() => {
    if (authCheckStatus === 'success' && authData) {
      if (authData.authenticated && authData.user) {
        setAuthState(authData.user, true);
      } else {
        localStorageUtils.clearSessionAuthenticated();
        clearUser();
        queryClient.removeQueries({ queryKey: authKeys.me() });
      }
    }

    if (authCheckStatus === 'error' || authError) {
      localStorageUtils.clearSessionAuthenticated();
      clearUser();
      queryClient.removeQueries({ queryKey: authKeys.me() });
    }
  }, [authCheckStatus, authData, authError, setAuthState, clearUser, queryClient]);

  // Use query data as source of truth when available, fall back to Zustand store.
  // This handles the brief window where the query has resolved but the
  // useEffect above hasn't flushed the update to Zustand yet.
  const effectiveIsAuthenticated = hasSession && authData ? authData.authenticated : isAuthenticated;
  const effectiveUser = hasSession && authData && authData.authenticated ? authData.user : user;

  // ── Redirect logic ────────────────────────────────────────────────
  const redirectToAppropriateRoute = useCallback(() => {
    // Don't navigate while auth check is in progress
    if (isAuthCheckPending) {
      return;
    }

    const currentPath = pathname;

    // Skip navigation logic for auth-callback page (handles its own redirects)
    if (currentPath === '/auth-callback') {
      return;
    }

    if (effectiveIsAuthenticated && effectiveUser && !isAuthCheckPending) {
      const dashboardUrl = getPoolsUrl();

      // Authenticated user on homepage → redirect to dashboard
      if (currentPath === '/') {
        if (dashboardUrl !== currentPath) {
          navigate(dashboardUrl);
        }
      }
    } else if (!effectiveIsAuthenticated && !isAuthCheckPending) {
      // Unauthenticated user on a protected route → redirect to homepage
      const protectedPaths = ['/pools'];
      if (protectedPaths.some((path) => currentPath.startsWith(path))) {
        // Close any open modals before redirecting
        closePoolModal();
        closeSignupModal();
        closeUserVerificationModal();
        useTwoFactorStore.getState().clear();

        // If user just logged out on initial page load,
        // don't store redirect URL and don't open login modal
        const isInitialLoad = !hasCheckedInitialLoad.current;
        const justLoggedOut = sessionStorageUtils.hasJustLoggedOut();

        if (!isInitialLoad && justLoggedOut) {
          sessionStorageUtils.clearJustLoggedOut();
        } else {
          // Store the current URL for redirect after login
          const currentUrl = location.pathname + location.search;
          sessionStorageUtils.setAfterLoginRedirectUrl(currentUrl);
          openLoginModal();
        }

        navigate('/');
      }
    }
  }, [
    effectiveIsAuthenticated,
    effectiveUser,
    pathname,
    navigate,
    isAuthCheckPending,
    openLoginModal,
    closePoolModal,
    closeSignupModal,
    closeUserVerificationModal,
    location,
  ]);

  // Track whether the initial auth check has completed so we can
  // distinguish "first load after refresh" from "session expired mid-use"
  useEffect(() => {
    if (!isAuthCheckPending) {
      hasCheckedInitialLoad.current = true;
    }
  }, [isAuthCheckPending]);

  // Trigger redirect evaluation whenever auth state or route changes
  useEffect(() => {
    if (!isAuthCheckPending) {
      redirectToAppropriateRoute();
    }
  }, [isAuthCheckPending, redirectToAppropriateRoute]);

  return null;
}
